/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { COLORS } from "./footer.constants";
import FooterBrand from "./FooterBrand";
import FooterLinks from "./FooterLinks";
import FooterContact from "./FooterContact";
import LegalDocumentModal from "../../legal/LegalDocumentModal";
import { LEGAL_DOCUMENTS, loadLegalDocument } from "../../../utils/legalDocuments";

export default function Footer() {
  const { t, i18n } = useTranslation();
  const isAR = i18n.language === "ar";
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalTitle, setLegalTitle] = useState("");
  const [legalContent, setLegalContent] = useState("");
  const [legalLoading, setLegalLoading] = useState(false);

  const scrollToSection = (id) => {
    const target =
      id === "support"
        ? document.getElementById("support-email")
        : document.getElementById(id);

    if (target) target.scrollIntoView({ behavior: "smooth" });
  };

  const openLegalDoc = async (docKey) => {
    const doc = LEGAL_DOCUMENTS[docKey];
    if (!doc) return;

    setLegalTitle(isAR ? doc.titleAr : doc.titleEn);
    setLegalContent("");
    setLegalLoading(true);
    setLegalModalOpen(true);

    try {
      const text = await loadLegalDocument(docKey, isAR ? "ar" : "en");
      setLegalContent(text);
    } catch (error) {
      setLegalContent(
        isAR ? "تعذر تحميل المستند حالياً." : "Unable to load the document right now."
      );
    } finally {
      setLegalLoading(false);
    }
  };

  return (
    <footer
      className="py-5"
      style={{
        background: COLORS.gradient,
        direction: isAR ? "rtl" : "ltr",
        fontFamily: "'Tajawal', 'Cairo', sans-serif",
        color: COLORS.textLight,
      }}
    >
      <div className="container">
        <div className="row text-md-start text-center align-items-start justify-content-between">
          <FooterBrand />
          <FooterLinks scrollToSection={scrollToSection} onOpenLegalDoc={openLegalDoc} />
          <FooterContact />
        </div>

        <hr
          style={{
            borderTop: "1px solid rgba(255,255,255,0.15)",
            margin: "25px 0",
          }}
        />

        <div
          className="text-center"
          style={{
            color: COLORS.gold,
            fontSize: ".95rem",
            fontWeight: "500",
          }}
        >
          © {new Date().getFullYear()}{" "}
          <span style={{ color: COLORS.textLight, fontWeight: "600" }}>
            {isAR ? "مدار المجرة للتجارة العامة والتجارة الإلكترونية وخدمات التوصيل" : "Madar Al-Majarra for General Trading, E-Commerce, and Delivery Services"}
          </span>{" "}
          — {t("footer.rights")}
        </div>
      </div>

      <LegalDocumentModal
        isOpen={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
        title={legalTitle}
        content={legalContent}
        loading={legalLoading}
        isArabic={isAR}
        themeColors={COLORS}
      />
    </footer>
  );
}
